import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CssBaseline from '@mui/material/CssBaseline';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GrassIcon from '@mui/icons-material/Grass';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import { PieChart } from '@mui/x-charts/PieChart';
import AppNavbar from '../dashboard/components/AppNavbar';
import SideMenu from '../dashboard/components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import { useAuth } from '../context/AuthContext';
import { useGrow } from '../dashboard/context/GrowContext';
import { DataModeProvider, DATA_MODES } from '../context/DataModeContext';
import { GrowProvider } from '../dashboard/context/GrowContext';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../dashboard/theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

// Plant type colors for pie chart
const PLANT_COLORS = {
  cucumber: '#22c55e',
  tomato: '#ef4444',
  pepper: '#f97316',
  lettuce: '#84cc16',
  herbs: '#10b981',
  strawberry: '#ec4899',
  microgreens: '#06b6d4',
  other: '#8b5cf6',
};

const PLANT_LABELS = {
  cucumber: 'Cucumber',
  tomato: 'Tomato',
  pepper: 'Pepper',
  lettuce: 'Lettuce',
  herbs: 'Herbs',
  strawberry: 'Strawberry',
  microgreens: 'Microgreens',
  other: 'Other',
};

function EditProfileDialog({ open, onClose, profile, onSave }) {
  const [displayName, setDisplayName] = React.useState('');
  const [bio, setBio] = React.useState('');
  const [website, setWebsite] = React.useState('');
  const [location, setLocation] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (open && profile) {
      setDisplayName(profile.displayName || '');
      setBio(profile.bio || '');
      setWebsite(profile.website || '');
      setLocation(profile.location || '');
    }
  }, [open, profile]);

  const handleSave = async () => {
    setLoading(true);
    await onSave({
      displayName: displayName.trim(),
      bio: bio.trim(),
      website: website.trim(),
      location: location.trim(),
    });
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { bgcolor: 'background.paper', backgroundImage: 'none' },
      }}
    >
      <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
        Edit Profile
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
              Display Name
            </Typography>
            <TextField
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fullWidth
              size="small"
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
              Bio
            </Typography>
            <TextField
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              fullWidth
              multiline
              rows={3}
              size="small"
              placeholder="Tell us about yourself..."
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
              Location
            </Typography>
            <TextField
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              fullWidth
              size="small"
              placeholder="City, Country"
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
              Website / Link
            </Typography>
            <TextField
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              fullWidth
              size="small"
              placeholder="https://..."
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ProfileContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, updateUserProfile } = useAuth();
  const { grows } = useGrow();
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);

  // Detect demo mode
  const isDemo = location.pathname.startsWith('/demo');
  const basePath = isDemo ? '/demo' : '/app';

  // Demo profile data
  const demoProfile = {
    displayName: 'Demo User',
    bio: 'Passionate indoor grower specializing in hydroponic systems. Always experimenting with new techniques and sharing knowledge with the community.',
    website: 'https://mygrowcollective.com',
    location: 'Denver, CO',
    createdAt: '2024-01-15T00:00:00Z',
    followers: 127,
    following: 84,
  };

  const profile = isDemo ? demoProfile : (userProfile || {});
  const displayName = isDemo ? demoProfile.displayName : (user?.displayName || profile.displayName || 'User');

  // Calculate grow stats
  const growStats = React.useMemo(() => {
    const stats = {
      total: grows.length,
      active: 0,
      completed: 0,
      cancelled: 0,
      plantTypes: {},
    };

    grows.forEach((grow) => {
      // Count by status
      if (grow.status === 'completed') {
        stats.completed++;
      } else if (grow.status === 'cancelled') {
        stats.cancelled++;
      } else {
        stats.active++;
      }

      // Count by plant type
      const plantType = grow.traits?.plantType || 'other';
      stats.plantTypes[plantType] = (stats.plantTypes[plantType] || 0) + 1;
    });

    return stats;
  }, [grows]);

  // Prepare pie chart data
  const pieChartData = React.useMemo(() => {
    return Object.entries(growStats.plantTypes).map(([type, count], index) => ({
      id: index,
      value: count,
      label: PLANT_LABELS[type] || type,
      color: PLANT_COLORS[type] || PLANT_COLORS.other,
    }));
  }, [growStats.plantTypes]);

  const handleEditSave = async (updates) => {
    if (!isDemo) {
      await updateUserProfile(updates);
    }
  };

  const handleGrowClick = (growId) => {
    navigate(`${basePath}/grow/${growId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />;
      case 'cancelled':
        return <CancelIcon sx={{ fontSize: 18, color: 'error.main' }} />;
      default:
        return <PendingIcon sx={{ fontSize: 18, color: 'warning.main' }} />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Active';
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1200px' }, mx: 'auto', p: 3 }}>
      {/* Profile Header Card */}
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'flex-start' }}>
          {/* Avatar and basic info */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 120,
                height: 120,
                bgcolor: 'primary.main',
                fontSize: '3rem',
                mb: 2,
              }}
            >
              {displayName[0]?.toUpperCase()}
            </Avatar>
            <Stack direction="row" spacing={3} sx={{ textAlign: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
                  {profile.followers || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Followers
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
                  {profile.following || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Following
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Profile details */}
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
                  {displayName}
                </Typography>
                {profile.location && (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                    <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {profile.location}
                    </Typography>
                  </Stack>
                )}
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                onClick={() => setEditDialogOpen(true)}
                sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif' }}
              >
                Edit Profile
              </Button>
            </Stack>

            {/* Bio */}
            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
                fontFamily: 'Inter, sans-serif',
                mb: 2,
                lineHeight: 1.6,
              }}
            >
              {profile.bio || 'No bio yet. Click Edit Profile to add one.'}
            </Typography>

            {/* Website link */}
            {profile.website && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 2 }}>
                <LinkIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                <Link
                  href={profile.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}
                >
                  {profile.website.replace(/^https?:\/\//, '')}
                </Link>
              </Stack>
            )}

            {/* Member since */}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Member since {formatDate(profile.createdAt)}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Card>

      {/* Stats Section */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 3 }}>
        {/* Grow Stats Card */}
        <Card variant="outlined" sx={{ p: 3, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 2 }}>
            Grow Statistics
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'action.hover',
                textAlign: 'center',
                minWidth: 80,
              }}
            >
              <GrassIcon sx={{ fontSize: 28, color: 'primary.main', mb: 0.5 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
                {growStats.total}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Total Grows
              </Typography>
            </Box>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha('#f59e0b', 0.1),
                textAlign: 'center',
                minWidth: 80,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif', color: 'warning.main' }}>
                {growStats.active}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Active
              </Typography>
            </Box>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha('#22c55e', 0.1),
                textAlign: 'center',
                minWidth: 80,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif', color: 'success.main' }}>
                {growStats.completed}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Completed
              </Typography>
            </Box>
          </Stack>
        </Card>

        {/* Plant Types Chart */}
        <Card variant="outlined" sx={{ p: 3, flex: 1, minHeight: 250 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 2 }}>
            Plants Grown
          </Typography>
          {pieChartData.length > 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 180 }}>
              <PieChart
                series={[
                  {
                    data: pieChartData,
                    innerRadius: 40,
                    outerRadius: 70,
                    paddingAngle: 2,
                    cornerRadius: 4,
                    highlightScope: { faded: 'global', highlighted: 'item' },
                  },
                ]}
                width={300}
                height={180}
                slotProps={{
                  legend: {
                    direction: 'column',
                    position: { vertical: 'middle', horizontal: 'right' },
                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    markGap: 5,
                    itemGap: 8,
                    labelStyle: {
                      fontSize: 12,
                      fontFamily: 'Inter, sans-serif',
                    },
                  },
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 180,
                color: 'text.secondary',
              }}
            >
              <Typography variant="body2">No grows yet</Typography>
            </Box>
          )}
        </Card>
      </Stack>

      {/* Grows Table */}
      <Card variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 2 }}>
          My Grows
        </Typography>
        {grows.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Plant Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Stage</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grows.map((grow) => (
                  <TableRow
                    key={grow.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleGrowClick(grow.id)}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'Inter, sans-serif' }}>
                        {grow.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={PLANT_LABELS[grow.traits?.plantType] || grow.traits?.plantType || 'Unknown'}
                        size="small"
                        sx={{
                          bgcolor: alpha(PLANT_COLORS[grow.traits?.plantType] || PLANT_COLORS.other, 0.2),
                          color: PLANT_COLORS[grow.traits?.plantType] || PLANT_COLORS.other,
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {formatDate(grow.startDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {grow.currentStage}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {getStatusIcon(grow.status)}
                        <Typography variant="body2">
                          {getStatusLabel(grow.status)}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              py: 4,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <GrassIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">No grows yet. Start your first grow!</Typography>
          </Box>
        )}
      </Card>

      {/* Edit Profile Dialog */}
      <EditProfileDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        profile={profile}
        onSave={handleEditSave}
      />
    </Box>
  );
}

export default function ProfilePage(props) {
  const location = useLocation();
  const isDemo = location.pathname.startsWith('/demo');

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <DataModeProvider mode={isDemo ? DATA_MODES.DEMO : DATA_MODES.LIVE}>
          <GrowProvider>
            <SideMenu />
            <AppNavbar />
            <Box
              component="main"
              sx={(theme) => ({
                flexGrow: 1,
                backgroundColor: theme.vars
                  ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                  : alpha(theme.palette.background.default, 1),
                overflow: 'auto',
                minHeight: '100vh',
              })}
            >
              <Stack
                sx={{
                  pt: { xs: 10, md: 3 },
                  pb: 5,
                }}
              >
                <ProfileContent />
              </Stack>
            </Box>
          </GrowProvider>
        </DataModeProvider>
      </Box>
    </AppTheme>
  );
}
